import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarApi, customerApi, orderApi, settingsApi } from '../api';
import { takeOrderPayment } from '../beamPayment';
import { Empty, ErrorNote, Modal, Spinner, dateTh, money, nowHhmm, todayIso, useLoader } from '../components/common';
import { IconSearch, IconChevronDown } from '../components/icons';
import PaymentMethodPicker from '../components/PaymentMethodPicker';

const GENDERS = [
  { value: 'female', glyph: '♀', className: 'g-female' },
  { value: 'male', glyph: '♂', className: 'g-male' },
  { value: 'other', glyph: '⚧', className: 'g-other' },
];

/**
 * ซื้อสินค้า (/buy-product) and ซื้อแพ็กเกจ (/buy-package).
 *
 * Full pages, not dialogs — they reuse the booking builder's shell: the booker
 * header on top, catalog on the left, running bill on the right. The footer
 * carries only ชำระเงิน, because a product sale has nothing to leave on the
 * board the way a booking does.
 */
export default function SalePage({ mode }) {
  const isPackage = mode === 'package';
  const navigate = useNavigate();

  const products = useLoader(
    () => (isPackage ? Promise.resolve(null) : settingsApi.products()), [mode],
  );
  const packages = useLoader(
    () => (isPackage ? settingsApi.packages() : Promise.resolve(null)), [mode],
  );
  const lookups = useLoader(() => calendarApi.lookups(), []);
  const general = useLoader(() => settingsApi.general(), []);

  const [date] = useState(todayIso());
  const [time] = useState(nowHhmm);
  const [showMore, setShowMore] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(true);
  const [buyer, setBuyer] = useState({ customerId: null, name: '', phone: '', gender: '', ageBandId: '', nationalityCode: '' });
  const [note, setNote] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [lines, setLines] = useState({});
  const [method, setMethod] = useState('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);

  const items = (
    isPackage
      ? (packages.data?.packages || []).filter((p) => p.is_available_at_branch !== false)
      : products.data?.products
  ) || [];
  const categories = isPackage ? [] : (products.data?.categories || []);
  const priceOf = (i) => Number(isPackage ? i.price : i.sell_price);
  const shown = items.filter((i) => {
    const t = search.trim().toLowerCase();
    const matchesCategory = category === 'all' || String(i.category_id) === category;
    return matchesCategory &&
      (!t || `${i.name_en || ''} ${i.name_th || ''} ${i.sku || i.code || ''}`.toLowerCase().includes(t));
  });
  const subtotal = items.reduce((s, i) => s + priceOf(i) * (lines[i.id] || 0), 0);
  const serviceChargeRate = general.data?.settings?.serviceChargeEnabled
    ? Number(general.data?.branch?.service_charge_rate || 0)
    : 0;
  const vatRate = Number(general.data?.branch?.vat_rate || 0);
  const serviceCharge = Math.round(subtotal * serviceChargeRate) / 100;
  const vat = general.data?.branch?.prices_include_vat
    ? 0
    : Math.round(subtotal * vatRate) / 100;
  const total = subtotal + serviceCharge + vat;
  const chosen = Object.entries(lines).filter(([, n]) => n > 0);

  const lookupBuyer = async (v) => {
    setQ(v);
    if (v.trim().length < 2) return setHits([]);
    try {
      const r = await customerApi.search(v);
      setHits(r.customers || []);
    } catch { setHits([]); }
  };

  const add = (item, delta) => setLines((l) => {
    const next = Math.max(0, (l[item.id] || 0) + delta);
    // A package is sold one at a time, so picking one clears the rest.
    return isPackage ? (next ? { [item.id]: 1 } : {}) : { ...l, [item.id]: next };
  });

  const validatePayment = () => {
    // The original checks the package owner first. Even with an empty package
    // basket its first response is the short customer validation below.
    if (isPackage && !buyer.customerId) {
      setError('กรุณากรอกข้อมูลลูกค้า');
      return false;
    }
    if (!chosen.length) {
      setError('กรุณาเลือกรายการอย่างน้อย 1 รายการ');
      return false;
    }
    return true;
  };

  const openPayment = () => {
    setError(null);
    if (validatePayment()) {
      setCashReceived(String(total));
      setPaymentOpen(true);
    }
  };

  const pay = async () => {
    if (!validatePayment()) return;
    if (method === 'cash' && Number(cashReceived) < total) {
      setError('จำนวนเงินสดที่ลูกค้าให้ยังไม่พอชำระ');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const order = await orderApi.create(isPackage
        ? { orderType: 'package', packageId: Number(chosen[0][0]), customerId: buyer.customerId || undefined, note: note || undefined }
        : {
          orderType: 'product',
          customerId: buyer.customerId || undefined,
          note: note || undefined,
          products: chosen.map(([id, quantity]) => ({ productId: Number(id), quantity })),
        });
      await takeOrderPayment({
        orderId: order.order.id,
        method,
        amount: order.order.grand_total,
        onWaiting: () => setWaiting(true),
      });
      navigate('/history');
    } catch (err) {
      setError(err.message);
      setBusy(false);
      setWaiting(false);
    }
  };

  if (products.loading || packages.loading || general.loading) return <Spinner />;

  return (
    <div className="builder">
      <aside className="builder-menu">
        {!isPackage && (
          <div className="sale-category-tabs">
            <button
              className={category === 'all' ? 'active' : ''}
              onClick={() => setCategory('all')}
            >
              ทั้งหมด
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                className={category === String(c.id) ? 'active' : ''}
                onClick={() => setCategory(String(c.id))}
              >
                {c.name_th || c.name_en}
              </button>
            ))}
          </div>
        )}
        <div className="menu-filters" style={{ gridTemplateColumns: '1fr' }}>
          <label className="search-field">
            <IconSearch size={17} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isPackage ? 'ค้นหาแพ็กเกจ' : 'ค้นหาสินค้า หรือสแกนบาร์โค้ด'}
            />
          </label>
        </div>

        {shown.length === 0 ? <Empty icon="product" message="ไม่พบรายการ" /> : shown.map((i) => (
          <button className="menu-card sale-item-card" key={i.id} onClick={() => add(i, 1)}>
            <div className="menu-thumb" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="menu-name">{i.name_th || i.name_en}</div>
              <div className="duration-row" style={{ alignItems: 'center' }}>
                <strong style={{ flex: 1 }}>{money(priceOf(i))}</strong>
                {!!lines[i.id] && <span className="sale-item-count">{lines[i.id]}</span>}
              </div>
            </div>
          </button>
        ))}
      </aside>

      <section className="builder-main">
        <div className="builder-head">
          <strong>รายละเอียดคำสั่งซื้อ</strong>
          <button
            className="btn-ghost btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => setShowMore((v) => !v)}
          >
            <IconChevronDown size={15} className={showMore ? 'flip' : ''} />
            {showMore ? 'ย่อ' : 'ดูเพิ่มเติม'}
          </button>
        </div>

        {showMore && (
          <div className="builder-more">
            <div><span>ประเภท</span><b>{isPackage ? 'แพ็กเกจ' : 'สินค้า'}</b></div>
            <div><span>วันที่</span><b>{dateTh(date)}</b></div>
            <div><span>เวลา</span><b>{time} น.</b></div>
            {note && <div><span>หมายเหตุ</span><b>{note}</b></div>}
            <div><span>ลูกค้า</span><b>{buyer.name || 'ลูกค้าทั่วไป'}</b></div>
            <div><span>จำนวนรายการ</span><b>{Object.keys(lines).length}</b></div>
          </div>
        )}

        <div className="field">
          <label>เวลาซื้อ</label>
          <div className="sale-when">
            <span>{dateTh(date)}</span>
            <span>{time}</span>
          </div>
        </div>

        <div className="field" style={{ position: 'relative' }}>
          <label>ใส่ชื่อผู้จอง</label>
          <button
            className="customer-detail-toggle"
            type="button"
            aria-expanded={customerDetails}
            onClick={() => setCustomerDetails((shownDetails) => !shownDetails)}
          >
            กรอกรายละเอียดลูกค้า
          </button>
          <label className="search-field">
            <IconSearch size={17} />
            <input value={q} onChange={(e) => lookupBuyer(e.target.value)} placeholder="ลูกค้า" />
          </label>
          {hits.length > 0 && (
            <div className="typeahead">
              {hits.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => {
                    setBuyer({ ...buyer, customerId: c.id, name: c.nickname || c.first_name || '', phone: c.phone || '' });
                    setQ(c.nickname || c.first_name || '');
                    setHits([]);
                  }}
                >
                  <strong>{c.nickname || c.first_name}</strong>
                  <span>#{c.member_code} · {c.phone || '-'}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {customerDetails && <div className="ident-row">
          <input
            value={buyer.phone}
            onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
            placeholder="เบอร์โทรศัพท์"
          />
          {GENDERS.map((g) => (
            <button
              key={g.value}
              className={`gender-btn ${g.className}${buyer.gender === g.value ? ' on' : ''}`}
              onClick={() => setBuyer({ ...buyer, gender: buyer.gender === g.value ? '' : g.value })}
            >
              {g.glyph}
            </button>
          ))}
          <select
            value={buyer.ageBandId}
            onChange={(e) => setBuyer({ ...buyer, ageBandId: e.target.value })}
          >
            <option value="">อายุ</option>
            {(lookups.data?.ageBands || []).map((b) => (
              <option key={b.id} value={b.id}>{b.label_th || b.label_en}</option>
            ))}
          </select>
          <select
            value={buyer.nationalityCode}
            onChange={(e) => setBuyer({ ...buyer, nationalityCode: e.target.value })}
          >
            <option value="">สัญชาติ</option>
            {(lookups.data?.nationalities || []).map((n) => (
              <option key={n.code} value={n.code}>{n.name_th || n.name_en}</option>
            ))}
          </select>
        </div>}

        <div className="field">
          <label>หมายเหตุ <span style={{ color: 'var(--ink-400)', fontWeight: 400 }}>(หมายเหตุเฉพาะการจองนี้)</span></label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="หมายเหตุ" />
        </div>

        {error && <ErrorNote error={{ message: error }} />}

        <div className="slot service" style={{ minHeight: 120 }}>
          {chosen.length === 0 ? (
            <div className="slot-empty">
              <strong>{isPackage ? 'แพ็กเกจ' : 'สินค้า'}</strong>
              <span>
                {isPackage ? 'เลือกแพ็กเกจฝั่งซ้าย' : 'เลือกสินค้าจากฝั่งซ้ายมือ หรือสแกนบาร์โค้ด'}
              </span>
            </div>
          ) : chosen.map(([id, qty]) => {
            const item = items.find((i) => String(i.id) === id);
            return (
              <div className="line-row" key={id}>
                <div className="line-name">
                  <strong>{item?.name_th || item?.name_en}</strong>
                  <span>{money(priceOf(item))} × {qty}</span>
                </div>
                <strong>{money(priceOf(item) * qty)}</strong>
                <button className="btn-ghost btn-sm" onClick={() => setLines((l) => ({ ...l, [id]: 0 }))}>✕</button>
              </div>
            );
          })}
        </div>

        <div className="builder-foot">
          <span className="foot-total">ราคารวม <strong>{money(total)}</strong></span>
          {/* No บันทึก here — a sale has nothing to park on the board. */}
          <button className="btn-solid" disabled={busy} onClick={openPayment}>
            ชำระเงิน
          </button>
        </div>
      </section>

      {paymentOpen && (
        <Modal
          title="ชำระเงิน"
          onClose={() => setPaymentOpen(false)}
          footer={
            <>
              <button className="btn-outline" onClick={() => setPaymentOpen(false)} disabled={waiting}>
                ยกเลิก
              </button>
              <button className="btn-solid" onClick={pay} disabled={busy}>
                {waiting ? 'กำลังรอ Beam…' : busy ? 'กำลังบันทึก…' : 'ยืนยันการชำระเงิน'}
              </button>
            </>
          }
        >
          <div className="cash-dialog-balance">
            <span>ยอดชำระสุทธิ</span>
            <strong>{money(total)}</strong>
          </div>
          <PaymentMethodPicker
            method={method}
            onMethod={setMethod}
            amount={total}
            cashReceived={cashReceived}
            onCashReceived={setCashReceived}
            waiting={waiting}
          />
        </Modal>
      )}
    </div>
  );
}
